import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../hooks/useFeatures';
import { UpgradePrompt } from '../components/UpgradePrompt';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Brain,
  Lightning,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Sparkle,
  SpinnerGap,
  Trophy,
  Target,
  TrendUp,
  Calendar,
  Star
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';
import { showNewBadges } from '../components/BadgeToast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SmartReviewPage = () => {
  const { user } = useAuth();
  const { hasFeature, pricing, loading: featuresLoading } = useFeatures();
  const [stage, setStage] = useState('intro'); // intro, review, results
  const [stats, setStats] = useState(null);
  const [weakAreas, setWeakAreas] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sessionResults, setSessionResults] = useState([]);
  const [aiExplanation, setAiExplanation] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [statsRes, weakRes] = await Promise.all([
        axios.get(`${API}/review/stats`, { withCredentials: true }),
        axios.get(`${API}/review/weak-areas`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setWeakAreas(weakRes.data);
    } catch (error) {
      console.error('Failed to fetch review stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const startReview = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/review/due?limit=10`, { withCredentials: true });
      setQuestions(data.questions);
      setStage('review');
      setCurrentIndex(0);
      setSelectedAnswer(null);
      setResult(null);
      setSessionResults([]);
      setAiExplanation(null);
    } catch (error) {
      console.error('Failed to fetch review questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async () => {
    if (selectedAnswer === null) return;
    setSubmitting(true);
    
    try {
      // Determine quality based on answer (simplified - can be expanded with self-rating)
      const { data } = await axios.post(`${API}/review/answer`, {
        question_id: questions[currentIndex].id,
        selected_answer: selectedAnswer,
        quality: selectedAnswer === result?.correct_answer ? 4 : 1  // Will be set after we get result
      }, { withCredentials: true });
      
      setResult(data);
      if (data.new_badges?.length) {
        showNewBadges(data.new_badges);
      }
      setSessionResults(prev => [...prev, {
        correct: data.correct,
        interval: data.spaced_repetition.new_interval
      }]);
    } catch (error) {
      console.error('Failed to submit answer:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGetAIExplanation = async () => {
    if (!user?.ai_enabled) return;
    setLoadingAI(true);
    
    try {
      const { data } = await axios.post(`${API}/ai/explanation`, {
        question_id: questions[currentIndex].id,
        selected_answer: selectedAnswer
      }, { withCredentials: true });
      setAiExplanation(data.explanation);
    } catch (error) {
      console.error('Failed to get AI explanation:', error);
    } finally {
      setLoadingAI(false);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setResult(null);
      setAiExplanation(null);
    } else {
      setStage('results');
    }
  };

  const rateQuality = async (quality) => {
    // Allow user to self-rate their confidence (affects spaced repetition more accurately)
    try {
      await axios.post(`${API}/review/answer`, {
        question_id: questions[currentIndex].id,
        selected_answer: selectedAnswer,
        quality: quality
      }, { withCredentials: true });
    } catch (error) {
      console.error('Failed to update quality rating:', error);
    }
    handleNext();
  };

  if (!featuresLoading && !hasFeature('smart_review')) {
    return <UpgradePrompt feature="smart_review" requiredTier="standard" pricing={pricing} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <SpinnerGap className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  // Intro/Stats Screen
  if (stage === 'intro') {
    const correctCount = sessionResults.filter(r => r.correct).length;
    
    return (
      <div className="min-h-screen bg-background p-4 md:p-8" data-testid="smart-review-page">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Brain weight="fill" className="w-8 h-8 text-primary" />
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                Spaced Repetition
              </p>
            </div>
            <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">
              Smart Review
            </h1>
            <p className="text-muted-foreground mt-2">
              AI-powered learning that focuses on your weak areas
            </p>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard 
                icon={Target} 
                label="Due Now" 
                value={stats.due_now}
                highlight={stats.due_now > 0}
              />
              <StatCard 
                icon={Brain} 
                label="Learning" 
                value={stats.learning}
              />
              <StatCard 
                icon={TrendUp} 
                label="Reviewing" 
                value={stats.reviewing}
              />
              <StatCard 
                icon={Trophy} 
                label="Mastered" 
                value={stats.mastered}
                color="text-success"
              />
            </div>
          )}

          {/* Progress Overview */}
          {stats && (
            <div className="bg-card border border-border rounded-sm p-6 mb-8">
              <h2 className="font-heading font-bold text-lg mb-4">Learning Progress</h2>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Questions in Rotation</span>
                    <span>{stats.total_reviewed} / {stats.total_questions}</span>
                  </div>
                  <Progress 
                    value={(stats.total_reviewed / stats.total_questions) * 100} 
                    className="h-2 rounded-none" 
                  />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Mastery Rate</span>
                    <span>{stats.mastery_percentage}%</span>
                  </div>
                  <Progress 
                    value={stats.mastery_percentage} 
                    className="h-2 rounded-none bg-muted" 
                  />
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-border">
                  <span className="text-muted-foreground">Average Ease Factor</span>
                  <span className="font-mono">{stats.average_ease_factor}</span>
                </div>
              </div>
            </div>
          )}

          {/* Weak Areas */}
          {weakAreas.length > 0 && (
            <div className="bg-card border border-border rounded-sm p-6 mb-8">
              <h2 className="font-heading font-bold text-lg mb-4">Areas to Focus On</h2>
              <div className="space-y-3">
                {weakAreas.slice(0, 5).map((area, index) => (
                  <div 
                    key={area.category}
                    className="flex items-center justify-between p-3 bg-muted rounded-sm"
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-8 h-8 rounded-sm flex items-center justify-center text-xs font-bold",
                        area.difficulty_level === 'Hard' 
                          ? "bg-destructive/20 text-destructive"
                          : area.difficulty_level === 'Medium'
                          ? "bg-amber-500/20 text-amber-500"
                          : "bg-success/20 text-success"
                      )}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{area.category}</p>
                        <p className="text-xs text-muted-foreground">
                          {area.questions_reviewed} reviewed • {area.struggling_count} struggling
                        </p>
                      </div>
                    </div>
                    <span className={cn(
                      "text-xs font-bold uppercase px-2 py-1 rounded-sm",
                      area.difficulty_level === 'Hard' 
                        ? "bg-destructive/20 text-destructive"
                        : area.difficulty_level === 'Medium'
                        ? "bg-amber-500/20 text-amber-500"
                        : "bg-success/20 text-success"
                    )}>
                      {area.difficulty_level}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* How It Works */}
          <div className="bg-accent/10 border border-accent/30 rounded-sm p-6 mb-8">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Lightning weight="fill" className="w-5 h-5 text-accent" />
              How Smart Review Works
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle weight="fill" className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                Questions you struggle with appear more frequently
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle weight="fill" className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                Mastered questions are shown less often
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle weight="fill" className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                The algorithm adapts to your learning speed
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle weight="fill" className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                Review daily for optimal retention
              </li>
            </ul>
          </div>

          {/* Start Button */}
          <Button
            onClick={startReview}
            disabled={loading}
            className="w-full rounded-sm h-12 font-bold"
            data-testid="start-review-button"
          >
            {loading ? (
              <SpinnerGap className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Brain weight="fill" className="w-5 h-5 mr-2" />
                Start Smart Review ({stats?.due_now || 0} due)
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Results Screen
  if (stage === 'results') {
    const correctCount = sessionResults.filter(r => r.correct).length;
    const accuracy = Math.round((correctCount / sessionResults.length) * 100);
    
    return (
      <div className="min-h-screen bg-background p-4 md:p-8" data-testid="review-results">
        <div className="max-w-2xl mx-auto text-center">
          <div className={cn(
            "w-24 h-24 rounded-sm flex items-center justify-center mx-auto mb-6",
            accuracy >= 70 ? "bg-success/20" : "bg-amber-500/20"
          )}>
            {accuracy >= 70 ? (
              <Trophy weight="fill" className="w-12 h-12 text-success" />
            ) : (
              <Brain weight="fill" className="w-12 h-12 text-amber-500" />
            )}
          </div>
          
          <h1 className="font-heading font-black text-3xl tracking-tighter mb-2">
            Review Complete!
          </h1>
          <p className="text-muted-foreground mb-8">
            {accuracy >= 70 
              ? "Great job! Keep up the consistent practice." 
              : "Keep practicing - repetition is key to mastery!"}
          </p>

          <div className="bg-card border border-border rounded-sm p-6 mb-8">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-3xl font-heading font-black text-primary">{correctCount}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Correct</p>
              </div>
              <div>
                <p className="text-3xl font-heading font-black">{sessionResults.length}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
              </div>
              <div>
                <p className="text-3xl font-heading font-black">{accuracy}%</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Accuracy</p>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              onClick={() => {
                fetchStats();
                setStage('intro');
              }}
              variant="outline"
              className="flex-1 rounded-sm"
            >
              View Stats
            </Button>
            <Button
              onClick={startReview}
              className="flex-1 rounded-sm"
              data-testid="continue-review"
            >
              Continue Reviewing
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Review Screen
  const currentQuestion = questions[currentIndex];
  const progressPercent = ((currentIndex + 1) / questions.length) * 100;

  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Brain weight="fill" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="font-heading font-bold text-xl mb-2">No Questions Due</h2>
          <p className="text-muted-foreground mb-4">Great job! Come back later for more reviews.</p>
          <Link to="/dashboard">
            <Button className="rounded-sm">Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="review-active">
      {/* Header */}
      <div className="bg-card border-b border-border p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => setStage('results')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">End Review</span>
          </button>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              {currentIndex + 1} / {questions.length}
            </span>
            {currentQuestion.is_new && (
              <span className="text-xs bg-accent/20 text-accent px-2 py-1 rounded-sm font-bold">
                NEW
              </span>
            )}
          </div>
        </div>
        <div className="max-w-4xl mx-auto mt-4">
          <Progress value={progressPercent} className="h-1 rounded-none" />
        </div>
      </div>

      {/* Question */}
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <div className="animate-slide-in">
          <div className="flex items-center gap-2 mb-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {currentQuestion.category}
            </p>
            {!currentQuestion.is_new && (
              <span className="text-xs text-muted-foreground">
                • Interval: {currentQuestion.interval}d
              </span>
            )}
          </div>
          <h2 className="font-heading font-bold text-xl md:text-2xl tracking-tight mb-8">
            {currentQuestion.question_text}
          </h2>

          {/* Question Image */}
          {currentQuestion.image_url && (
            <div className="mb-8 flex justify-center">
              <img
                src={currentQuestion.image_url}
                alt="Question illustration"
                className="max-w-xs w-full rounded-sm border border-border bg-muted p-2"
                data-testid="question-image"
              />
            </div>
          )}

          {/* Options */}
          <div className="space-y-3 mb-8">
            {currentQuestion.options.map((option, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrect = result && index === result.correct_answer;
              const isWrong = result && isSelected && !result.correct;

              return (
                <button
                  key={index}
                  onClick={() => !result && setSelectedAnswer(index)}
                  disabled={!!result}
                  className={cn(
                    "quiz-option w-full text-left",
                    isSelected && !result && "selected",
                    isCorrect && "correct",
                    isWrong && "incorrect"
                  )}
                  data-testid={`review-option-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 border",
                      isSelected && !result && "bg-primary border-primary text-primary-foreground",
                      isCorrect && "bg-success border-success text-white",
                      isWrong && "bg-destructive border-destructive text-white",
                      !isSelected && !isCorrect && !isWrong && "border-border"
                    )}>
                      {isCorrect ? (
                        <CheckCircle weight="fill" className="w-5 h-5" />
                      ) : isWrong ? (
                        <XCircle weight="fill" className="w-5 h-5" />
                      ) : (
                        <span className="font-bold text-sm">{String.fromCharCode(65 + index)}</span>
                      )}
                    </div>
                    <span className="pt-1">{option}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Result & Actions */}
          {result && (
            <div className="animate-fade-in mb-8">
              <div className={cn(
                "p-4 rounded-sm border mb-4",
                result.correct 
                  ? "bg-success/10 border-success/30" 
                  : "bg-destructive/10 border-destructive/30"
              )}>
                <div className="flex items-center gap-2 mb-2">
                  {result.correct ? (
                    <CheckCircle weight="fill" className="w-5 h-5 text-success" />
                  ) : (
                    <XCircle weight="fill" className="w-5 h-5 text-destructive" />
                  )}
                  <span className="font-bold">
                    {result.correct ? 'Correct!' : 'Incorrect'}
                  </span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    Next review in {result.spaced_repetition.new_interval} day(s)
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{result.explanation}</p>
              </div>

              {/* AI Explanation */}
              {user?.ai_enabled && (
                <div className="space-y-3 mb-4">
                  {!aiExplanation ? (
                    <Button
                      onClick={handleGetAIExplanation}
                      disabled={loadingAI}
                      variant="outline"
                      className="rounded-sm ai-glow"
                    >
                      {loadingAI ? (
                        <SpinnerGap className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Sparkle weight="fill" className="w-4 h-4 mr-2 text-accent" />
                      )}
                      Get AI Explanation
                    </Button>
                  ) : (
                    <div className="p-4 bg-accent/10 border border-accent/30 rounded-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkle weight="fill" className="w-5 h-5 text-accent" />
                        <span className="font-bold text-sm">AI Tutor</span>
                      </div>
                      <p className="text-sm">{aiExplanation}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Self-Rating (optional for more accurate SR) */}
              <div className="p-4 bg-muted rounded-sm mb-4">
                <p className="text-sm font-medium mb-3">How well did you know this?</p>
                <div className="flex gap-2">
                  {[
                    { label: 'Forgot', quality: 0, color: 'bg-destructive' },
                    { label: 'Hard', quality: 2, color: 'bg-amber-500' },
                    { label: 'Good', quality: 3, color: 'bg-blue-500' },
                    { label: 'Easy', quality: 5, color: 'bg-success' },
                  ].map((rating) => (
                    <button
                      key={rating.quality}
                      onClick={() => rateQuality(rating.quality)}
                      className={cn(
                        "flex-1 py-2 rounded-sm text-xs font-bold text-white transition-transform hover:scale-105",
                        rating.color
                      )}
                      data-testid={`rate-${rating.label.toLowerCase()}`}
                    >
                      {rating.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Submit/Next Actions */}
          {!result ? (
            <Button
              onClick={submitAnswer}
              disabled={selectedAnswer === null || submitting}
              className="w-full rounded-sm"
              data-testid="submit-review-answer"
            >
              {submitting ? (
                <SpinnerGap className="w-5 h-5 animate-spin" />
              ) : (
                'Check Answer'
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="w-full rounded-sm"
              data-testid="next-review-question"
            >
              {currentIndex < questions.length - 1 ? (
                <>
                  Next Question
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              ) : (
                <>
                  Finish Review
                  <CheckCircle className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color = "text-primary", highlight = false }) => (
  <div className={cn(
    "bg-card border rounded-sm p-4",
    highlight ? "border-primary" : "border-border"
  )}>
    <div className="flex items-center gap-3">
      <Icon weight="fill" className={cn("w-8 h-8", color)} />
      <div>
        <p className={cn("font-heading font-bold text-2xl tracking-tight", highlight && "text-primary")}>
          {value}
        </p>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
      </div>
    </div>
  </div>
);
